import { FC } from "react";
import { HiViewGrid, HiViewList, HiOutlinePlus } from "react-icons/hi";
import { Button } from "../Button/Button";
import { HelpLabel } from "../HelpLabel/HelpLabel";
import { StoryblokHelpBlock } from "../StoryblokHelpBlock/StoryblokHelpBlock";
import type { CatalogViewMode } from "../../hooks/useCatalogViewMode";

export type { CatalogViewMode };

export interface InventoryCatalogHeaderProps {
  title: string;
  description: string;
  helpStorySlug: string;
  helpAccessToken?: string;
  viewMode: CatalogViewMode;
  onViewModeChange: (mode: CatalogViewMode) => void;
  gridViewTitle: string;
  listViewTitle: string;
  createButtonText: string;
  onCreate: () => void;
  /** Defaults to the vehicles create-button classes. */
  createButtonClassName?: string;
  /** Defaults to the vehicles create-button icon classes. */
  createButtonIconClassName?: string;
}

const DEFAULT_HELP_ACCESS_TOKEN = "Jf9NMAu4DXeo3fwdjd1w7wtt";

/**
 * Shared catalog header (title + help + grid/list toggle + create button) used
 * by the Vehicles and Products inventory pages.
 */
export const InventoryCatalogHeader: FC<InventoryCatalogHeaderProps> = ({
  title,
  description,
  helpStorySlug,
  helpAccessToken = DEFAULT_HELP_ACCESS_TOKEN,
  viewMode,
  onViewModeChange,
  gridViewTitle,
  listViewTitle,
  createButtonText,
  onCreate,
  createButtonClassName = "font-medium w-full sm:w-auto",
  createButtonIconClassName = "mr-2 h-5 w-5",
}) => (
  <div className="p-4">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-text sm:text-2xl">
            {title}
          </h1>
          <HelpLabel
            content={
              <StoryblokHelpBlock
                storySlug={helpStorySlug}
                accessToken={helpAccessToken}
              />
            }
            className="mt-1"
          />
        </div>
        <p className="text-secondary text-base pt-2">
          {description}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button.Group className="w-full sm:w-auto">
            <Button
              color={viewMode === "grid" ? "purple" : "gray"}
              onClick={() => onViewModeChange("grid")}
              title={gridViewTitle}
              className="flex-1 sm:flex-auto"
            >
              <HiViewGrid className="h-5 w-5" />
            </Button>
            <Button
              color={viewMode === "list" ? "purple" : "gray"}
              onClick={() => onViewModeChange("list")}
              title={listViewTitle}
              className="flex-1 sm:flex-auto"
            >
              <HiViewList className="h-5 w-5" />
            </Button>
          </Button.Group>
        </div>
        <Button color="light" onClick={onCreate} className={createButtonClassName}>
          <HiOutlinePlus className={createButtonIconClassName} />
          {createButtonText}
        </Button>
      </div>
    </div>
  </div>
);
